import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  applyLoginResponse,
  applySessionProfile,
  clearAuthSession,
  clearLegacyAuthKeys,
  getAccessToken,
  getRefreshToken,
  getUidFromAccessToken,
  hasKnownAssignedBranches,
  isAccessTokenExpired,
  setAccessToken,
  setRefreshToken,
  type AssignedBranch,
  type SessionUser,
} from '../auth-session.util';
import { authEpochStore, isSessionResultCurrent } from '../auth-epoch.store';
import {
  readSessionFlag,
  shouldAttemptSessionRestore,
} from '../session-restore.util';

import { SKIP_FEEDBACK } from '../../../shared/http/feedback.context';
import { LoginResponse, LoginOtpResendResponse } from '../login-otp.util';

const OFFICE_AUTH_HTTP_OPTIONS = {
  withCredentials: true,
  headers: { 'X-Auth-Audience': 'office' },
} as const;

/** Response from POST /auth/office/password-status */
export interface OfficePasswordStatusResponse {
  email: string;
  passwordSet: boolean;
  firstLogin: boolean;
  // BLK-06: `isActive` and `approvalStatus` were removed from this response.
  // Nothing here ever read them, and together they told an attacker which
  // accounts were claimable. Do not re-add them.
}

/**
 * `GET /auth/office/me` — who this session is, for the branch switcher.
 *
 * `user` is `null` for a legacy office/branch token (no `uid` claim), and
 * `assignedBranches` is `[]` for an HQ employee.
 */
export interface OfficeMeResponse {
  role: 'office' | 'branch';
  sub: string;
  user: SessionUser | null;
  assignedBranches: AssignedBranch[];
  office?: Record<string, unknown>;
  branch?: AssignedBranch;
}

/** BLK-06: what `office/verify-sms` returns for an office with no password. */
export interface OfficeSetupProofResponse {
  message?: string;
  setupToken: string;
  expiresAt?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  baseUrl = environment.baseUrl;
  private readonly rawHttp: HttpClient;
  private bootstrapPromise: Promise<boolean> | null = null;

  constructor(
    private http: HttpClient,
    httpBackend: HttpBackend,
  ) {
    this.rawHttp = new HttpClient(httpBackend);
  }

  isAuthenticated(): boolean {
    return !!getAccessToken();
  }

  /**
   * Restore session from HttpOnly refresh cookie (always attempted when access token missing/expired).
   */
  async bootstrapSession(): Promise<boolean> {
    if (this.bootstrapPromise) {
      return this.bootstrapPromise;
    }

    this.bootstrapPromise = this.ensureAccessToken()
      .then(async (ok) => {
        if (ok) {
          await this.hydrateEmployeeProfile();
        }
        return ok;
      })
      .finally(() => {
        this.bootstrapPromise = null;
      });

    return this.bootstrapPromise;
  }

  /**
   * Repopulate `user` + `assignedBranches` after a RELOAD.
   *
   * Both live in memory (mirrored to localStorage for the first paint), so a
   * browser that cleared its storage — or a tab restored from a session that
   * predates the mirror — comes back with a valid refresh cookie and no idea
   * that the operator is an employee with three branches. The switcher would
   * then be silently absent, which reads as "this account cannot switch".
   *
   * Asked ONCE and only when it is needed: the token must carry a `uid` (a
   * legacy office/branch login has none and `me` would answer `user: null`),
   * and the assignments must be genuinely UNKNOWN rather than merely empty —
   * an HQ employee legitimately has `[]`, and re-asking on every load for that
   * would be a wasted round trip on the critical path of every navigation.
   *
   * It fails soft: no switcher is better than no portal.
   */
  private async hydrateEmployeeProfile(): Promise<void> {
    if (this.meFetched || hasKnownAssignedBranches()) {
      return;
    }
    if (!getUidFromAccessToken()) {
      return;
    }
    this.meFetched = true;
    try {
      const res = await firstValueFrom(this.me());
      applySessionProfile(res);
    } catch {
      /* no switcher is better than no portal */
    }
  }

  private meFetched = false;

  private async ensureAccessToken(): Promise<boolean> {
    const accessToken = getAccessToken();
    if (accessToken && !isAccessTokenExpired(accessToken)) {
      return true;
    }
    // BLK-36: a visitor who has never signed in on this browser has no refresh
    // cookie, so asking for one is a guaranteed 401 that the login page used to
    // wait behind. The flag is a hint only — the cookie is still the authority.
    if (!shouldAttemptSessionRestore(readSessionFlag())) {
      return false;
    }
    return this.refreshSession();
  }

  /** POST /auth/refresh-token — in-memory token + HttpOnly cookie fallback. */
  async refreshSession(): Promise<boolean> {
    if (AuthService.refreshInFlight) {
      return AuthService.refreshInFlight;
    }

    // BLK-35: captured BEFORE the request goes out. Nothing here can be
    // cancelled, so the result is judged on arrival instead.
    const startedAtEpoch = authEpochStore.current();

    AuthService.refreshInFlight = (async () => {
      try {
        const refreshToken =
          getRefreshToken() ||
          localStorage.getItem('refreshToken')?.trim() ||
          undefined;
        const data = await firstValueFrom(
          this.rawHttp.post<{ accessToken?: string; refreshToken?: string }>(
            `${this.baseUrl}/auth/refresh-token`,
            refreshToken ? { refreshToken } : {},
            {
              ...OFFICE_AUTH_HTTP_OPTIONS,
            },
          ),
        );
        if (!data?.accessToken) {
          return false;
        }
        // The user logged out while this was in the air: writing the token now
        // would resurrect the session they just ended, and guestGuard would
        // bounce them off the login page.
        if (!isSessionResultCurrent(startedAtEpoch)) {
          return false;
        }
        setAccessToken(data.accessToken);
        if (data.refreshToken) {
          setRefreshToken(data.refreshToken);
        }
        clearLegacyAuthKeys();
        return true;
      } catch {
        return false;
      } finally {
        AuthService.refreshInFlight = null;
      }
    })();

    return AuthService.refreshInFlight;
  }

  private static refreshInFlight: Promise<boolean> | null = null;

  loginWithEmailPassword(email: string, password: string, fcmTokens: string[] = []) {
    return this.http.post<LoginResponse>(
      `${this.baseUrl}/auth/office/login`,
      { email, password, fcmTokens },
      { ...OFFICE_AUTH_HTTP_OPTIONS, context: new HttpContext().set(SKIP_FEEDBACK, true) },
    );
  }

  verifyLoginOtp(challengeToken: string, code: string, fcmTokens: string[] = []): Observable<Record<string, unknown>> {
    return this.http.post<Record<string, unknown>>(
      `${this.baseUrl}/auth/office/verify-login-otp`,
      { challengeToken, code, fcmTokens },
      { ...OFFICE_AUTH_HTTP_OPTIONS, context: new HttpContext().set(SKIP_FEEDBACK, true) },
    );
  }

  /**
   * Turn the 5-minute selection token into a session for ONE branch.
   *
   * PUBLIC and unauthenticated — the login that produced the token minted no
   * access token and set no refresh cookie, which is why `auth.interceptor`
   * exempts this URL from both the Bearer attachment and the 401 refresh. The
   * refusals are all `401 BRANCH_SELECTION_INVALID`, so errors are inline
   * (`SKIP_FEEDBACK`) exactly as on login.
   */
  selectBranch(
    selectionToken: string,
    branchId: string,
    fcmTokens: string[] = [],
  ): Observable<Record<string, unknown>> {
    return this.http.post<Record<string, unknown>>(
      `${this.baseUrl}/auth/office/select-branch`,
      { selectionToken, branchId, fcmTokens },
      { ...OFFICE_AUTH_HTTP_OPTIONS, context: new HttpContext().set(SKIP_FEEDBACK, true) },
    );
  }

  /**
   * Move a live employee session to another assigned branch.
   *
   * AUTHENTICATED, deliberately: it is `@Roles('branch','office')` and needs
   * the `uid` claim, so it must NOT be in the interceptor's auth-exempt list.
   * No `refreshToken` in the body — a browser presents it in the httpOnly
   * cookie, and the server revokes exactly that session (other devices stay
   * signed in).
   */
  switchBranch(
    branchId: string,
    fcmTokens: string[] = [],
  ): Observable<Record<string, unknown>> {
    return this.http.post<Record<string, unknown>>(
      `${this.baseUrl}/auth/office/switch-branch`,
      { branchId, fcmTokens },
      { ...OFFICE_AUTH_HTTP_OPTIONS, context: new HttpContext().set(SKIP_FEEDBACK, true) },
    );
  }

  /** Who this session is — the only way to render the switcher after a reload. */
  me(): Observable<OfficeMeResponse> {
    return this.http.get<OfficeMeResponse>(
      `${this.baseUrl}/auth/office/me`,
      { ...OFFICE_AUTH_HTTP_OPTIONS, context: new HttpContext().set(SKIP_FEEDBACK, true) },
    );
  }

  resendLoginOtp(challengeToken: string): Observable<LoginOtpResendResponse> {
    return this.http.post<LoginOtpResendResponse>(
      `${this.baseUrl}/auth/office/resend-login-otp`,
      { challengeToken },
      { ...OFFICE_AUTH_HTTP_OPTIONS, context: new HttpContext().set(SKIP_FEEDBACK, true) },
    );
  }

  getOfficePasswordStatus(
    email: string,
  ): Observable<OfficePasswordStatusResponse> {
    const url = `${this.baseUrl}/auth/office/password-status`;
    return this.http.post<OfficePasswordStatusResponse>(url, { email });
  }

  /**
   * BLK-06: the office is identified by the single-use `setupToken` from
   * `officeVerifySms`, not by an email. Sending an email here is now refused
   * for an office — that was the account-takeover path.
   */
  setOfficeFirstPassword(
    setupToken: string,
    password: string,
    fcmTokens: string[] = [],
  ) {
    const url = `${this.baseUrl}/auth/office/set-password`;
    return this.http.post<Record<string, unknown>>(
      url,
      { setupToken, password, fcmTokens },
      OFFICE_AUTH_HTTP_OPTIONS,
    );
  }

  /**
   * BLK-06: send a first-password code to the office's REGISTERED email. The
   * reply is uniform whatever the email, so it can never confirm an account.
   */
  requestOfficeOtp(email: string) {
    const url = `${this.baseUrl}/auth/office/request-otp`;
    return this.http.post<{ message: string }>(url, { email });
  }

  createOffice(formData: FormData) {
    const url = `${this.baseUrl}/auth/office/signup`;
    return this.http.post(url, formData);
  }

  officeVerifySms(email: string, code: string) {
    const url = `${this.baseUrl}/auth/office/verify-sms`;
    return this.http.post<OfficeSetupProofResponse>(
      url,
      // Routed signup still identifies the account by phone; login uses email.
      email.includes('@') ? { email, code } : { phone: email, code },
      OFFICE_AUTH_HTTP_OPTIONS,
    );
  }

  completeOfficeProfile(data: Record<string, unknown>) {
    const url = `${this.baseUrl}/auth/office/complete-profile`;
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      const value = data[key];
      if (value instanceof File) {
        formData.append(key, value);
      } else if (Array.isArray(value)) {
        value.forEach((item) => formData.append(`${key}[]`, String(item)));
      } else if (value !== null && value !== undefined) {
        formData.append(key, String(value));
      }
    });

    return this.http.post(url, formData, OFFICE_AUTH_HTTP_OPTIONS);
  }

  register(body: unknown) {
    return this.http.post(`${this.baseUrl}/api/auth/register`, body);
  }

  validateOTP(body: unknown) {
    return this.http.post(`${this.baseUrl}/admin/validate-otp`, body);
  }

  requestPasswordReset(email: unknown) {
    return this.http.post(`${this.baseUrl}/api/auth/request-password-reset`, email);
  }

  verifyOtp(email: string, otp: number) {
    return this.http.post(`${this.baseUrl}/api/auth/verify-otp`, { email, otp });
  }

  verifyResetPasswordOtp(phone: string, code: number) {
    return this.http.post(`${this.baseUrl}/auth/verify-sms`, { phone, code });
  }

  setResetPasswordNewPassword(email: string, password: string) {
    return this.http.post(`${this.baseUrl}/api/auth/set-reset-password-new-password`, {
      email,
      password,
    });
  }

  changeForgetPassword(body: unknown) {
    return this.http.post(`${this.baseUrl}/forget/password/change`, body);
  }

  changeDefaultPassword(body: unknown) {
    return this.http.post(`${this.baseUrl}/admin/change-default-password`, body);
  }

  changeOldPassword(body: unknown, id: number | string) {
    return this.http.post(`${this.baseUrl}admin/change-password/${id}`, body);
  }

  getSports() {
    return this.http.get(`${this.baseUrl}/api/sports`);
  }

  getCountries() {
    return this.http.get(`${this.baseUrl}/api/countries`);
  }

  completeSignup(data: Record<string, unknown>) {
    const url = `${this.baseUrl}/auth/complete-signup`;
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      const value = data[key];
      if (value instanceof File) {
        formData.append(key, value);
      } else if (Array.isArray(value)) {
        value.forEach((item) => formData.append(`${key}[]`, String(item)));
      } else if (value !== null && value !== undefined) {
        formData.append(key, String(value));
      }
    });

    return this.http.post(url, formData);
  }

  handleAuthResponse(res: Record<string, unknown>): void {
    applyLoginResponse(res);
  }
}
